import React, { useState, useEffect } from 'react';
import { get } from '../../../services/smartApiService';
import { OPTIONS_URLS } from '../../../api/OptionsUrls';

interface TechnicalData {
  symbol: string;
  spot_price: number;
  day_change: number;
  day_change_percent: number;
  moving_averages: {
    sma_20: number;
    sma_50: number;
    sma_200: number;
    ema_20: number;
    ema_50: number;
    signal: string;
  };
  rsi: { value: number; signal: string };
  macd: { line: number; signal_line: number; histogram: number; signal: string };
  bollinger_bands: { upper: number; middle: number; lower: number; signal: string };
  pivot_points: { pivot: number; r1: number; r2: number; r3: number; s1: number; s2: number; s3: number };
  atr: { value: number; signal: string };
  stochastic: { k: number; d: number; signal: string };
  adx: { value: number; signal: string };
  summary: { overall_signal: string; buy_count: number; sell_count: number; neutral_count: number };
  calculated_at: string;
}

interface Props {
  symbol: string;
  onDataLoaded?: (data: TechnicalData) => void;
}

const TechnicalIndicators: React.FC<Props> = ({ symbol, onDataLoaded }) => {
  const [data, setData] = useState<TechnicalData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadIndicators = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await get(OPTIONS_URLS.TECHNICAL_INDICATORS(symbol, refresh), { showLoading: false }).toPromise();
      if (response?.data) {
        setData(response.data);
        onDataLoaded?.(response.data);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load indicators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (symbol) {
      loadIndicators();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const getSignalColor = (signal: string) => {
    const s = signal?.toLowerCase() || '';
    if (s.includes('bull') || s.includes('buy') || s.includes('oversold')) return 'is-success';
    if (s.includes('bear') || s.includes('sell') || s.includes('overbought')) return 'is-danger';
    return 'is-warning';
  };

  const getSignalIcon = (signal: string) => {
    const s = signal?.toLowerCase() || '';
    if (s.includes('bull') || s.includes('buy') || s.includes('oversold')) return '▲';
    if (s.includes('bear') || s.includes('sell') || s.includes('overbought')) return '▼';
    return '●';
  };

  if (loading && !data) {
    return (
      <div className="has-text-centered py-3">
        <button className="button is-loading is-white is-small"></button>
        <p className="is-size-7 mt-1">Loading indicators...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification is-danger is-light py-2 px-3">
        <p className="is-size-7">{error}</p>
        <button onClick={() => loadIndicators(true)} className="button is-small is-danger mt-2">Retry</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="notification is-light py-2 px-3 has-text-centered">
        <p className="is-size-7">No data available</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Bar */}
      <div className="columns is-multiline mb-2" style={{ margin: '0 -0.25rem' }}>
        <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
          <div className={`notification py-2 px-3 mb-0 ${getSignalColor(data.summary.overall_signal)} is-light`}>
            <p className="heading is-size-7 mb-0">Overall</p>
            <p className="is-size-6 has-text-weight-bold mb-0">
              {getSignalIcon(data.summary.overall_signal)} {data.summary.overall_signal?.toUpperCase()}
            </p>
          </div>
        </div>
        <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
          <div className="notification is-light py-2 px-3 mb-0">
            <p className="heading is-size-7 mb-0">Signals</p>
            <p className="is-size-7 mb-0">
              <span className="has-text-success has-text-weight-bold">{data.summary.buy_count} Buy</span> |
              <span className="has-text-danger has-text-weight-bold ml-1">{data.summary.sell_count} Sell</span> |
              <span className="has-text-grey ml-1">{data.summary.neutral_count} Neutral</span>
            </p>
          </div>
        </div>
        <div className="column is-4 py-1" style={{ padding: '0 0.25rem' }}>
          <div className="notification is-light py-2 px-3 mb-0">
            <p className="heading is-size-7 mb-0">Volatility</p>
            <p className="is-size-7 has-text-weight-bold mb-0">
              {data.atr?.signal?.toUpperCase()} (ATR: {data.atr?.value?.toFixed(2)})
            </p>
          </div>
        </div>
      </div>

      {/* Indicators Table */}
      <div className="table-container">
        <table className="table is-fullwidth is-narrow is-hoverable" style={{ fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th className="py-1">Indicator</th>
              <th className="py-1">Value</th>
              <th className="py-1">Signal</th>
            </tr>
          </thead>
          <tbody>
            {/* RSI */}
            <tr>
              <td className="py-1"><strong>RSI (14)</strong></td>
              <td className="py-1">{data.rsi?.value?.toFixed(2)}</td>
              <td className="py-1">
                <span className={`tag is-small ${getSignalColor(data.rsi?.signal)}`}>
                  {getSignalIcon(data.rsi?.signal)} {data.rsi?.signal}
                </span>
              </td>
            </tr>

            {/* MACD */}
            <tr>
              <td className="py-1"><strong>MACD</strong></td>
              <td className="py-1">
                {data.macd?.line?.toFixed(2)} / {data.macd?.signal_line?.toFixed(2)}
              </td>
              <td className="py-1">
                <span className={`tag is-small ${getSignalColor(data.macd?.signal)}`}>
                  {getSignalIcon(data.macd?.signal)} {data.macd?.signal}
                </span>
              </td>
            </tr>

            {/* Moving Averages */}
            <tr>
              <td className="py-1"><strong>MA (20/50)</strong></td>
              <td className="py-1">
                {data.moving_averages?.sma_20?.toFixed(2)} / {data.moving_averages?.sma_50?.toFixed(2)}
              </td>
              <td className="py-1">
                <span className={`tag is-small ${getSignalColor(data.moving_averages?.signal)}`}>
                  {getSignalIcon(data.moving_averages?.signal)} {data.moving_averages?.signal}
                </span>
              </td>
            </tr>

            {/* Bollinger Bands */}
            <tr>
              <td className="py-1"><strong>Bollinger</strong></td>
              <td className="py-1">
                {data.bollinger_bands?.lower?.toFixed(0)} - {data.bollinger_bands?.upper?.toFixed(0)}
              </td>
              <td className="py-1">
                <span className={`tag is-small ${getSignalColor(data.bollinger_bands?.signal)}`}>
                  {getSignalIcon(data.bollinger_bands?.signal)} {data.bollinger_bands?.signal}
                </span>
              </td>
            </tr>

            {/* Stochastic */}
            <tr>
              <td className="py-1"><strong>Stochastic</strong></td>
              <td className="py-1">
                K: {data.stochastic?.k?.toFixed(2)} / D: {data.stochastic?.d?.toFixed(2)}
              </td>
              <td className="py-1">
                <span className={`tag is-small ${getSignalColor(data.stochastic?.signal)}`}>
                  {getSignalIcon(data.stochastic?.signal)} {data.stochastic?.signal}
                </span>
              </td>
            </tr>

            {/* ADX */}
            <tr>
              <td className="py-1"><strong>ADX (14)</strong></td>
              <td className="py-1">{data.adx?.value?.toFixed(2)}</td>
              <td className="py-1">
                <span className={`tag is-small ${data.adx?.value > 25 ? 'is-info' : 'is-light'}`}>
                  {data.adx?.signal}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pivot Points */}
      <div className="columns is-multiline mb-0 mt-2" style={{ margin: '0 -0.25rem' }}>
        <div className="column is-6 py-1" style={{ padding: '0 0.25rem' }}>
          <div className="box has-background-danger-light py-2 px-3 mb-0">
            <p className="heading is-size-7 mb-1">Resistance</p>
            <p className="is-size-7 mb-0">
              <span className="tag is-small is-danger mr-1">R1: {data.pivot_points?.r1?.toFixed(0)}</span>
              <span className="tag is-small is-danger mr-1">R2: {data.pivot_points?.r2?.toFixed(0)}</span>
              <span className="tag is-small is-danger">R3: {data.pivot_points?.r3?.toFixed(0)}</span>
            </p>
          </div>
        </div>
        <div className="column is-6 py-1" style={{ padding: '0 0.25rem' }}>
          <div className="box has-background-success-light py-2 px-3 mb-0">
            <p className="heading is-size-7 mb-1">Support</p>
            <p className="is-size-7 mb-0">
              <span className="tag is-small is-success mr-1">S1: {data.pivot_points?.s1?.toFixed(0)}</span>
              <span className="tag is-small is-success mr-1">S2: {data.pivot_points?.s2?.toFixed(0)}</span>
              <span className="tag is-small is-success">S3: {data.pivot_points?.s3?.toFixed(0)}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="level mt-2 mb-0">
        <div className="level-left">
          <p className="is-size-7 has-text-grey">
            Pivot: {data.pivot_points?.pivot?.toFixed(0)} | Updated: {data.calculated_at ? new Date(data.calculated_at).toLocaleTimeString() : '-'}
          </p>
        </div>
        <div className="level-right">
          <button
            onClick={() => loadIndicators(true)}
            disabled={loading}
            className={`button is-small is-info is-outlined ${loading ? 'is-loading' : ''}`}
          >
            <span className="icon is-small"><i className="fa fa-refresh"></i></span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechnicalIndicators;
